import React, { useEffect, useMemo, useRef, useState } from 'react';
import { injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, Icon, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import cx from 'classnames';
import { NotificationManager } from 'Components/Notifications';
import { defaultProductName } from 'Core/defaultValues';
import AppIcon from '../appIcon';
// Actions
import { accountAppsCheckApiKey, accountAppsCreate, accountAppsSearchCatalog, openAppsWizard } from '../../redux/slice';
import { getProductName, keyAccepted, maskApiKey } from '../../constants';
import './styles.scss';

// Common first step: choosing the app. The app's own steps come from its wizardConfig.
const PICK_STEP = { type: 'pick', label: 'accountAppsWizard_stepApp', sub: 'accountAppsWizard_stepAppSub' };
// Pace of the connection checks: one API call answers all of them, each check stays visible at least this long
const CHECK_STEP_MS = 1000;

/**
 * Proposal B · Blueprint: guided modal with a live row preview.
 * "App" (common) → the selected app's own steps from its wizardConfig (API catalog, fleet.integrations):
 * "credentials" (API key, verified inline), "verify" (connection checks before saving) and "sync"
 * (vehicle sync summary). The last step creates the integration (and starts the vehicle sync when the
 * app has one). Texts of the configuration are translation keys of this module (en/es).
 */
const NewIntegrationWizard = (props) => {
    const { intl: { messages }, integrations = [] } = props;
    const dispatch = useDispatch();
    const timers = useRef([]);
    const verifyRun = useRef(0);
    const keyInput = useRef();
    const { catalog, catalogLoading, catalogError } = useSelector((state) => state.accountAppsRedux);
    const reseller = useSelector((state) => state.resellerRedux?.current_reseller);
    const productName = getProductName(reseller, defaultProductName);

    const [stepIdx, setStepIdx] = useState(0);
    const [pickId, setPickId] = useState(null);
    const [pickQ, setPickQ] = useState('');
    const [key, setKey] = useState('');
    const [keyShow, setKeyShow] = useState(false);
    const [keyErr, setKeyErr] = useState(null);
    // Inline "Verify" of the credentials step: idle | checking | ok | fail | unreachable, for the key in kvKey
    const [kv, setKv] = useState('idle');
    const [kvKey, setKvKey] = useState('');
    // Connection checks of the verify step
    const [vIdx, setVIdx] = useState(0);
    const [vFail, setVFail] = useState(false);
    const [failKind, setFailKind] = useState(null);
    // Answer of the key check (account and vehicle count) shown by the credentials and verify steps
    const [check, setCheck] = useState(null);
    const [saving, setSaving] = useState(false);

    const later = (fn, ms) => timers.current.push(setTimeout(fn, ms));
    const clearTimers = () => {
        timers.current.forEach(clearTimeout);
        timers.current = [];
        verifyRun.current += 1;
    };
    useEffect(() => {
        dispatch(accountAppsSearchCatalog());
        return clearTimers;
    }, []);

    const app = catalog.find((item) => item.code === pickId) || null;
    const appName = app?.name || '';
    const config = app?.wizardConfig;
    const steps = config ? [PICK_STEP, ...config.steps] : [PICK_STEP];
    const step = steps[stepIdx] || PICK_STEP;
    const isLastStep = stepIdx === steps.length - 1;
    const credentialsIdx = steps.findIndex((item) => item.type === 'credentials');
    const verifyIdx = steps.findIndex((item) => item.type === 'verify');
    const field = steps[credentialsIdx]?.fields?.[0];
    const keyMax = field?.maxLength || app?.apiKeyMaxLength;
    const checks = steps[verifyIdx]?.checks || [];

    useEffect(() => {
        if (step.type === 'credentials') keyInput.current?.focus();
    }, [stepIdx]);

    // Translation of a key of this module (UI texts and wizardConfig keys), with the app and product names
    const tr = (textKey, name = appName) =>
        (textKey && messages[textKey] ? messages[textKey] : '').replace(/_APP_/g, name || '').replace(/_PRODUCT_/g, productName);

    const connectedCodes = useMemo(() => new Set(integrations.map((item) => item.integrationCode)), [integrations]);
    const vDone = vIdx >= checks.length && !vFail;
    const keyTrim = key.trim();
    const kvOk = kv === 'ok' && kvKey === keyTrim;
    const kvFail = (kv === 'fail' || kv === 'unreachable') && kvKey === keyTrim;
    const kvStale = (kv === 'ok' || kv === 'fail') && !kvOk && !kvFail && !!keyTrim;
    const failNet = vFail && failKind === 'net';
    // The row preview shows the integration as Active once the key passed every check of the app's wizard
    const keyVerified = verifyIdx >= 0 ? stepIdx > verifyIdx || (stepIdx === verifyIdx && vDone) : kvOk;

    const pq = pickQ.trim().toLowerCase();
    const pickTiles = catalog
        .filter((item) => !pq || [item.name, tr(item.wizardConfig.presentation?.description, item.name)].some((text) => (text || '').toLowerCase().includes(pq)));
    const available = catalog.filter((item) => !connectedCodes.has(item.code)).length;
    const pickCountLabel = (available === 1 ? messages['accountAppsWizard_appAvailable'] : messages['accountAppsWizard_appsAvailable'].replace('_COUNT_', available))
        + (connectedCodes.size ? messages['accountAppsWizard_appsConnected'].replace('_COUNT_', connectedCodes.size) : '');

    const close = () => {
        if (saving) return;
        clearTimers();
        dispatch(openAppsWizard(false));
    };

    const pickApp = (code) => {
        if (connectedCodes.has(code)) return;
        setPickId((current) => (current === code ? null : code));
    };

    const goToPick = () => {
        clearTimers();
        setStepIdx(0);
        setPickId(null);
    };

    const goToCredentials = () => {
        clearTimers();
        setStepIdx(credentialsIdx);
    };

    // Inline "Verify" button of the credentials step
    const verifyKey = async () => {
        const value = keyTrim;
        if (!value || kv === 'checking' || !app) return;
        setKv('checking');
        setKvKey(value);
        setKeyErr(null);
        const run = verifyRun.current;
        try {
            const result = await dispatch(accountAppsCheckApiKey({ integrationCode: app.code, apiKey: value })).unwrap();
            if (run === verifyRun.current) {
                setCheck(result);
                setKv(keyAccepted(result) ? 'ok' : 'fail');
            }
        } catch (e) {
            if (run === verifyRun.current) setKv('unreachable');
        }
    };

    // Verify step: runs the app's connection checks before anything is saved. One API call answers them:
    // an unreachable provider fails the first check, a rejected key the second one (or the only one), a key that
    // can't read the fleet's vehicles the third one; the account that accepted the key is reported on its check.
    const startVerify = () => {
        if (!keyTrim) return setKeyErr(messages['accountAppsWizard_keyRequired']);
        clearTimers();
        const run = verifyRun.current;
        setKey(keyTrim);
        setKeyErr(null);
        setStepIdx(verifyIdx);
        setVIdx(0);
        setVFail(false);
        setFailKind(null);
        setCheck(null);

        const started = Date.now();
        dispatch(accountAppsCheckApiKey({ integrationCode: app.code, apiKey: keyTrim })).unwrap()
            .then((result) => ({ result }))
            .catch(() => ({ unreachable: true }))
            .then(({ result, unreachable }) => {
                if (run !== verifyRun.current) return;
                const first = Math.max(0, CHECK_STEP_MS - (Date.now() - started));
                if (unreachable) return later(() => { setVFail(true); setFailKind('net'); }, first);
                setCheck(result);
                const reveal = (until) => {
                    for (let i = 1; i <= until; i++) later(() => setVIdx(i), first + (i - 1) * CHECK_STEP_MS);
                };
                if (!result.authenticated) {
                    const authIdx = Math.min(1, checks.length - 1);
                    // The credentials step shows the key as rejected when the user goes back to edit it
                    setKv('fail');
                    setKvKey(keyTrim);
                    reveal(authIdx);
                    return later(() => { setVFail(true); setFailKind('auth'); }, first + authIdx * CHECK_STEP_MS);
                }
                if (!keyAccepted(result)) {
                    // Without a vehicles check in the app's wizard, the authentication check reports it
                    const failIdx = checks.length >= 3 ? 2 : Math.min(1, checks.length - 1);
                    setKv('fail');
                    setKvKey(keyTrim);
                    reveal(failIdx);
                    return later(() => { setVFail(true); setFailKind('vehicles'); }, first + failIdx * CHECK_STEP_MS);
                }
                reveal(checks.length);
            });
    };

    const finish = async () => {
        if (saving) return;
        setSaving(true);
        try {
            await dispatch(accountAppsCreate({ integrationCode: app.code, apiKey: keyTrim, syncsVehicles: !!app.syncsVehicles })).unwrap();
            clearTimers();
            if (!app.syncsVehicles) {
                NotificationManager.success(tr('accountAppsWizard_connected'), messages['success'], 3500, null, null, 'success');
            }
        } catch (e) {
            setSaving(false);
            NotificationManager.error(e?.error || messages['errorMessage'], messages['error'], 5000, null, null, 'warning');
        }
    };

    // Moves to the next step of the app's wizard; the last one creates the integration
    const next = () => {
        if (isLastStep) return finish();
        if (steps[stepIdx + 1].type === 'verify') return startVerify();
        clearTimers();
        setStepIdx(stepIdx + 1);
    };

    // Footer primary button, per step type (same rules as the design)
    let primaryLabel = messages['accountAppsWizard_next'];
    let primaryAction = () => setStepIdx(1);
    let primaryDisabled = !app;
    if (step.type === 'credentials') {
        primaryLabel = isLastStep ? messages['accountAppsWizard_finish'] : messages['accountAppsWizard_next'];
        primaryDisabled = !kvOk || saving;
        primaryAction = () => { if (kvOk) next(); };
    }
    if (step.type === 'verify') {
        primaryLabel = vFail
            ? (failNet ? messages['accountAppsWizard_retry'] : messages['accountAppsWizard_editKey'])
            : vDone ? (isLastStep ? messages['accountAppsWizard_finish'] : messages['accountAppsWizard_continue']) : messages['accountAppsWizard_verifying'];
        primaryDisabled = (!vFail && !vDone) || saving;
        primaryAction = vFail ? (failNet ? startVerify : goToCredentials) : next;
    }
    if (step.type === 'sync') {
        primaryLabel = messages['accountAppsWizard_finish'];
        primaryAction = finish;
        primaryDisabled = saving;
    }
    const showBack = step.type === 'credentials' || (step.type === 'verify' && vFail);
    const back = step.type === 'credentials' ? goToPick : goToCredentials;

    const verifyTitle = vFail ? messages['accountAppsWizard_verifyTitleFailed'] : vDone ? messages['accountAppsWizard_verifyTitleDone'] : messages['accountAppsWizard_verifyTitleRunning'];
    const checksTotal = Math.max(1, checks.length);
    const vPct = vFail ? Math.round(vIdx / checksTotal * 100) : Math.min(100, Math.round(vIdx / checksTotal * 100) + (vDone ? 0 : 12));
    const keyCountClass = !keyMax ? '' : key.length >= keyMax ? 'is-max' : key.length >= keyMax * 0.9 ? 'is-near' : '';
    const presentation = config?.presentation || {};

    // What a passed check reports, by position in the app's checks (reach, auth, vehicles): the account that accepted
    // the key, and whether its fleet has vehicles (one page of 1, never a count). A fleet without vehicles, or an
    // access that could not be verified, is a warning that does not block
    const authCheckIdx = checks.length >= 2 ? 1 : -1;
    const vehiclesCheckIdx = checks.length >= 3 ? 2 : -1;
    const vehiclesNote = check?.hasVehicles === false ? 'none' : check && check.vehicleAccess == null ? 'unknown' : null;
    const vehiclesWarning = vDone && vehiclesCheckIdx >= 0 && !!vehiclesNote;
    const checkPassedText = (i) => {
        if (i === authCheckIdx && check?.accountName) return check.accountName;
        if (i === vehiclesCheckIdx && vehiclesNote) return messages[vehiclesNote === 'none' ? 'accountAppsWizard_checkVehiclesNone' : 'accountAppsWizard_checkVehiclesUnknown'];
        return messages['accountAppsWizard_checkPassed'];
    };

    return (
        <Dialog
            open
            onClose={(event, reason) => { if (reason !== 'backdropClick') close(); }}
            maxWidth={false}
            className="account-apps-wizard"
            PaperProps={{ className: 'account-apps-wizard-paper', 'aria-label': messages['accountAppsWizard_title'] }}
        >
            <aside className="wizard-rail">
                <div className="wizard-rail-title">{messages['accountAppsWizard_title']}</div>
                {
                    app ?
                        <div className="wizard-steps">
                            {steps.map((item, i) => {
                                const done = i < stepIdx || (i === stepIdx && step.type === 'verify' && vDone);
                                const current = i === stepIdx && !done;
                                return (
                                    <div key={`${item.type}-${i}`} className={cx('wizard-step', { 'is-done': done, 'is-current': current })}>
                                        <div className="wizard-step-marker">
                                            <span className="wizard-step-light">{done ? <Icon className="wizard-step-check">check</Icon> : i + 1}</span>
                                            {i < steps.length - 1 && <span className="wizard-step-line" />}
                                        </div>
                                        <div className="wizard-step-text">
                                            <div className="wizard-step-label">{tr(item.label)}</div>
                                            <div className="wizard-step-sub">{tr(item.sub)}</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        :
                        <div className="wizard-rail-empty">{messages['accountAppsWizard_selectApp']}</div>
                }
                <div className="wizard-preview">
                    <div className="wizard-preview-title">{messages['accountAppsWizard_rowPreview']}</div>
                    <div className="wizard-preview-sub">{messages['accountAppsWizard_rowPreviewSub']}</div>
                    <div className="wizard-preview-card">
                        <div className="wizard-preview-row">
                            <span className="wizard-preview-label">{messages['accountApps_name']}</span>
                            {
                                app ?
                                    <span className="wizard-preview-name">
                                        <AppIcon className="wizard-preview-icon" glyphClassName="wizard-preview-glyph" icon={presentation.icon} color={presentation.color} name={appName} />
                                        {appName}
                                    </span>
                                    : <span className="wizard-preview-bar" style={{ width: 70 }} />
                            }
                        </div>
                        <div className="wizard-preview-row">
                            <span className="wizard-preview-label">{messages['accountApps_apiKey']}</span>
                            {keyTrim ? <span className="wizard-preview-key">{maskApiKey(keyTrim)}</span> : <span className="wizard-preview-bar" style={{ width: 90 }} />}
                        </div>
                        <div className="wizard-preview-row">
                            <span className="wizard-preview-label">{messages['accountApps_status']}</span>
                            {keyVerified ? <span className="wizard-preview-pill">{messages['accountApps_active']}</span> : <span className="wizard-preview-bar" style={{ width: 50 }} />}
                        </div>
                    </div>
                </div>
            </aside>

            <section className="wizard-main">
                <div className="wizard-close-bar">
                    <Tooltip title={messages['accountAppsWizard_close']} placement="bottom">
                        <button type="button" className="wizard-icon-button" aria-label={messages['accountAppsWizard_close']} onClick={close}>
                            <Icon className="wizard-close-icon">close</Icon>
                        </button>
                    </Tooltip>
                </div>

                <div className="wizard-content">
                    {
                        step.type === 'pick' &&
                        <>
                            <div className="wizard-title">{tr('accountAppsWizard_pickTitle')}</div>
                            <div className="wizard-sub">{tr('accountAppsWizard_pickSub')}</div>
                            <div className="wizard-pick-bar">
                                <div className="wizard-pick-count">{catalogLoading ? messages['accountAppsWizard_loadingApps'] : pickCountLabel}</div>
                                <div className="wizard-pick-search">
                                    <Icon className="wizard-pick-search-icon">search</Icon>
                                    <input
                                        value={pickQ}
                                        onChange={(e) => setPickQ(e.target.value)}
                                        placeholder={messages['accountAppsWizard_searchApps']}
                                        aria-label={messages['accountAppsWizard_searchApps']}
                                    />
                                </div>
                            </div>
                            <div className="wizard-tiles">
                                {pickTiles.map((item) => {
                                    const connected = connectedCodes.has(item.code);
                                    const selected = pickId === item.code;
                                    const tile = item.wizardConfig.presentation || {};
                                    const description = tr(tile.description, item.name);
                                    return (
                                        <Tooltip key={item.code} title={connected ? messages['accountAppsWizard_alreadyConnected'] : tr('accountAppsWizard_connect', item.name)} placement="top">
                                            <span>
                                                <button
                                                    type="button"
                                                    className={cx('wizard-tile', { 'is-selected': selected, 'is-connected': connected })}
                                                    disabled={connected}
                                                    aria-pressed={selected}
                                                    onClick={() => pickApp(item.code)}
                                                >
                                                    <AppIcon className="wizard-tile-icon" glyphClassName="wizard-tile-glyph" icon={tile.icon} color={tile.color} name={item.name} />
                                                    <span className="wizard-tile-text">
                                                        <span className="wizard-tile-name">{item.name}</span>
                                                        {description && <span className="wizard-tile-sub">{description}</span>}
                                                    </span>
                                                    {selected && <Icon className="wizard-tile-check">check_circle</Icon>}
                                                    {connected && <Icon className="wizard-tile-connected">check_circle</Icon>}
                                                </button>
                                            </span>
                                        </Tooltip>
                                    );
                                })}
                            </div>
                            {catalogLoading && <div className="wizard-pick-empty"><Icon className="wizard-spin wizard-pick-loading">autorenew</Icon></div>}
                            {
                                !catalogLoading && catalogError &&
                                <div role="alert" className="wizard-pick-empty wizard-pick-error">
                                    {messages['accountAppsWizard_appsError']}
                                    <button type="button" onClick={() => dispatch(accountAppsSearchCatalog())}>{messages['accountAppsWizard_retry']}</button>
                                </div>
                            }
                            {!catalogLoading && !catalogError && !pickTiles.length && (
                                <div className="wizard-pick-empty">
                                    {pq ? messages['accountAppsWizard_noApps'].replace('_SEARCH_', pickQ) : messages['accountAppsWizard_noAppsAvailable']}
                                </div>
                            )}
                        </>
                    }

                    {
                        step.type === 'credentials' &&
                        <>
                            <div className="wizard-title">{tr(step.title)}</div>
                            {step.text && <div className="wizard-sub">{tr(step.text)}</div>}
                            <div className="wizard-key-label"><span>*</span>{tr(field?.label)}</div>
                            <div className="wizard-key-row">
                                <div className="wizard-key-field">
                                    <input
                                        ref={keyInput}
                                        type={keyShow ? 'text' : 'password'}
                                        value={key}
                                        onChange={(e) => { setKey(keyMax ? e.target.value.slice(0, keyMax) : e.target.value); setKeyErr(null); }}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { kvOk ? primaryAction() : verifyKey(); } }}
                                        maxLength={keyMax}
                                        placeholder={tr(field?.placeholder)}
                                        aria-label={tr(field?.label)}
                                        autoComplete="off"
                                        spellCheck={false}
                                        disabled={kv === 'checking'}
                                        className={cx({ 'is-error': kvFail || keyErr, 'is-ok': kvOk })}
                                    />
                                    <Tooltip title={keyShow ? messages['accountAppsWizard_hideKey'] : messages['accountAppsWizard_showKey']} placement="top">
                                        <button
                                            type="button"
                                            className="wizard-icon-button wizard-eye"
                                            aria-label={keyShow ? messages['accountAppsWizard_hideKey'] : messages['accountAppsWizard_showKey']}
                                            onClick={() => setKeyShow((show) => !show)}
                                        >
                                            {keyShow ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                                        </button>
                                    </Tooltip>
                                </div>
                                <button
                                    type="button"
                                    className={cx('wizard-verify', { 'is-empty': !keyTrim })}
                                    onClick={verifyKey}
                                    disabled={!keyTrim || kv === 'checking' || kvOk}
                                >
                                    {(kv === 'checking' || kvOk) && <Icon className={cx('wizard-verify-icon', { 'wizard-spin': kv === 'checking' })}>{kv === 'checking' ? 'autorenew' : 'check'}</Icon>}
                                    {kv === 'checking' ? messages['accountAppsWizard_verifying'] : kvOk ? messages['accountAppsWizard_verified'] : messages['accountAppsWizard_verify']}
                                </button>
                            </div>
                            {keyErr && <div role="alert" className="wizard-key-error">{keyErr}</div>}
                            {
                                (kvOk || kvFail || kvStale) &&
                                <div role="status" className={cx('wizard-key-status', { 'is-ok': kvOk, 'is-fail': kvFail, 'is-stale': kvStale })}>
                                    <Icon className="wizard-key-status-icon">{kvOk ? 'check_circle' : kvFail ? 'error' : 'warning'}</Icon>
                                    <span>
                                        {kvOk ? tr(check?.accountName ? 'accountAppsWizard_keyAcceptedAccount' : 'accountAppsWizard_keyAccepted').replace('_ACCOUNT_', check?.accountName || '')
                                            : kv === 'unreachable' && kvFail ? tr('accountAppsWizard_keyUnreachable')
                                                : kvFail ? tr(check?.authenticated ? 'accountAppsWizard_keyNoVehicles' : 'accountAppsWizard_keyRejected')
                                                    : messages['accountAppsWizard_keyStale']}
                                    </span>
                                </div>
                            }
                            <div className="wizard-key-hint">
                                <span>{tr(field?.hint)}</span>
                                {keyMax && <span className={cx('wizard-key-count', keyCountClass)}>{key.length} / {keyMax}</span>}
                            </div>
                            {
                                tr(step.help) &&
                                <div className="wizard-info">
                                    <InfoOutlinedIcon />
                                    <div>{tr(step.help)}</div>
                                </div>
                            }
                        </>
                    }

                    {
                        step.type === 'verify' &&
                        <>
                            <div className="wizard-title">{verifyTitle}</div>
                            {step.text && <div className="wizard-sub">{tr(step.text)}</div>}
                            <div className="wizard-progress">
                                <div className={cx('wizard-progress-bar', { 'is-fail': vFail, 'is-done': vDone })} style={{ width: `${vPct}%` }} />
                            </div>
                            <div className="wizard-checks">
                                {checks.map((label, i) => {
                                    const done = i < vIdx;
                                    const failed = vFail && i === vIdx;
                                    const running = !done && !failed && !vFail && i === vIdx;
                                    return (
                                        <div key={`${label}-${i}`} className={cx('wizard-check', { 'is-active': done || running || failed })}>
                                            {
                                                running
                                                    ? <Icon className="wizard-check-icon wizard-spin is-running">autorenew</Icon>
                                                    : <Icon className={cx('wizard-check-icon', { 'is-done': done, 'is-failed': failed })}>{done ? 'check_circle' : failed ? 'error' : 'radio_button_unchecked'}</Icon>
                                            }
                                            <span className="wizard-check-label">{tr(label)}</span>
                                            <span className={cx('wizard-check-status', { 'is-warning': done && i === vehiclesCheckIdx && !!vehiclesNote })}>
                                                {done ? checkPassedText(i) : failed ? messages['accountAppsWizard_checkFailed'] : running ? messages['accountAppsWizard_checkChecking'] : messages['accountAppsWizard_checkPending']}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                            {vFail && <div role="alert" className="wizard-fail">{tr(failNet ? 'accountAppsWizard_failNet' : failKind === 'vehicles' ? 'accountAppsWizard_failVehicles' : 'accountAppsWizard_failAuth')}</div>}
                            {
                                vehiclesWarning &&
                                <div role="status" className="wizard-check-warning">
                                    <Icon className="wizard-check-warning-icon">warning</Icon>
                                    <span>{tr(vehiclesNote === 'none' ? 'accountAppsWizard_noVehiclesWarning' : 'accountAppsWizard_vehiclesUnknownWarning')}</span>
                                </div>
                            }
                        </>
                    }

                    {
                        step.type === 'sync' &&
                        <>
                            <span className="wizard-verified"><Icon className="wizard-verified-icon">verified</Icon></span>
                            <div className="wizard-title wizard-sync-title">{tr(step.title)}</div>
                            {step.text && <div className="wizard-sync-text">{tr(step.text)}</div>}
                            <div className="wizard-timeline">
                                {(step.timeline || []).map((item, i, list) => (
                                    <div key={`${item.title}-${i}`} className={cx('wizard-timeline-item', { 'is-last': i === list.length - 1 })}>
                                        <div className="wizard-timeline-marker">
                                            <span className={cx('dot', `is-${item.status}`)} />
                                            {i < list.length - 1 && <span className="line" />}
                                        </div>
                                        <div className="wizard-timeline-text">
                                            <div className="label">{tr(item.title)}</div>
                                            {item.text && <div className="sub">{tr(item.text)}</div>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    }
                </div>

                <div className="wizard-footer">
                    <button type="button" className="wizard-text-button" onClick={close} disabled={saving}>{messages['accountAppsWizard_cancel']}</button>
                    <div className="wizard-footer-space" />
                    {showBack && <button type="button" className="wizard-text-button" onClick={back}>{messages['accountAppsWizard_back']}</button>}
                    <button type="button" className="wizard-primary" onClick={primaryAction} disabled={primaryDisabled}>
                        {primaryLabel}
                    </button>
                </div>
            </section>
        </Dialog>
    );
};

export default injectIntl(NewIntegrationWizard);
